import { assign, fromPromise, setup } from 'xstate';

import { logger } from '../../../../../helpers/logging';
import { sendAuthScreenViewedEvent, sendOAuthButtonClickedEvent } from '../../../helpers/events';
import { sendAuthButtonClickedEvent } from '../../../helpers/events/sendAuthButtonClickedEvent';

import { context as initialContext } from './data/context';
import { handleOAuth, handleOAuthError } from './helpers/oauth';
import { handleStandardAuth, handleStandardAuthError } from './helpers/standardAuth';
import type { Context, Events } from './types';

export const machine = setup({
  types: {} as { context: Context; events: Events; input: Partial<Context> },
  actions: {
    clearOAuthErrors: assign(({ context: { OAuth } }) => ({ OAuth: { ...OAuth, errors: [] } })),
    clearStandardAuthErrors: assign(({ context: { standardAuth } }) => ({
      standardAuth: { ...standardAuth, errors: [] },
    })),
    sendAuthScreenViewedEvent: ({ context }) => {
      sendAuthScreenViewedEvent('signup')(context);
    },
    sendAuthSignUpClickedEvent: ({ context }) => {
      sendAuthButtonClickedEvent('email', 'signup')(context);
    },
    sendOAuthButtonClickedEvent: ({ context }) => {
      sendOAuthButtonClickedEvent(context);
    },
    sendSwitchToLoginEvent: ({ context }) => {
      sendAuthButtonClickedEvent('login', 'signup')(context);
    },
    setAccount: assign(({ context: { standardAuth }, event }) => ({
      ...(event.type === 'SIGNUP_VIA_CREDENTIALS' && {
        standardAuth: { ...standardAuth, account: event.data.account },
      }),
    })),
    setIsLoading: assign({ isLoading: (_, params: { isLoading: boolean }) => params.isLoading }),
    setOAuth: assign(({ event }) => ({
      ...(event.type === 'SIGNUP_VIA_OAUTH' && {
        OAuth: { ...event.data.OAuth, errors: [], source: 'signup' as const },
      }),
    })),
    setOAuthErrors: assign(({ context: { OAuth }, event }) => ({
      ...(event.type === 'xstate.error.actor.handleOAuth' && {
        OAuth: { ...OAuth, errors: [{ errorMessage: handleOAuthError(event.error) }] },
      }),
    })),
    setOAuthMethod: assign(({ context: { OAuth }, event }) => ({
      ...(event.type === 'SIGNUP_VIA_CREDENTIALS' && {
        OAuth: { ...OAuth, method: event.data.OAuth.method },
      }),
    })),
    setReCaptchaRef: assign(({ context: { standardAuth }, event }) => ({
      ...(event.type === 'SIGNUP_VIA_CREDENTIALS' && {
        standardAuth: { ...standardAuth, reCaptcha: { ref: event.data.reCaptcha.ref } },
      }),
    })),
    setStandardAuthErrors: assign(({ context: { standardAuth }, event }) => {
      logger.debug('setStandardAuthErrors', standardAuth.errors);
      return {
        ...(event.type === 'xstate.error.actor.handleStandardAuth' && {
          standardAuth: {
            ...standardAuth,
            errors: [{ errorMessage: handleStandardAuthError(event.error) }],
          },
        }),
      };
    }),
    setUserIsLoggedInTrue: assign(({ context: { user } }) => ({
      user: { ...user, isLoggedIn: true },
    })),
  },
  guards: { isUserLoggedIn: ({ context: { user } }) => user.isLoggedIn },
  actors: {
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    handleStandardAuth: fromPromise<void, Context>(async ({ input }) => handleStandardAuth(input)),
    handleOAuth: fromPromise<boolean, Context>(async ({ input }) => handleOAuth(input)),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    resetReCaptcha: fromPromise<void, Context>(async ({ input: { standardAuth } }) => {
      const { ref } = standardAuth.reCaptcha;
      if (ref?.current) {
        await ref.current.reset();
      }
    }),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5SwJZQHYFUAOBZAhgMYAWK6YAdAE5j4QCeAxANoAMAuoqNgPaoAuKHui4gAHogC0AVgoAmAGzSFcgIwBmAJwAWdeukB2TdOkAaEPURyD6iqw2ttqw9NUAOdW4C+X86gw4BCRklDR0TMyqnEggvAJCIjESCM6sFNLaLsZymloGcuaWCJ6a8qxurJo6jhqG2j5+aFh4RKTkFCQ8fGC4YPzEPBCMAMoAkgDiAHKYAAoA+gBqowCCcwDCAEoAogAiW5MAKisAMsNs0dx8KILCosmqrGlu1o-uBtqVigUWiNpVFJpVPlNIpPAYDNJWNIGiB-M0gm1KJ1ur1+oMRhNpvMlqsAPLLTAHAAS51EcWuCTuiA8cjsyk0bmM6gMjN0hUQkNp0jk2m0RihzmsmhhcMCrRCHQGKL6AyGwwA6qMDmsiXMDri5sdceNRpNSTFyTdEqBkn9tBQIW5MoppG4TBp2QhFKoKIytOo5OUPODhb5YU0xcF2rAAK4AIwAttdBOgoGsaBAwOhBPgADawRgQYSUMgANx4AGtKMR8OgIKmwMN+KWIPgqBBliH+vrLvFbkkrG43HZdKwFBUDFCTLlHblaZkFCDVApWAYFDPvH7RS0g5RQ5Ho2Q4wmkyn04wwFQqDwqBRsKn8PwAGYniMUEtlitVmt1htN4gt2JXI1U4ogij9n2ugaICbiqKojqchQziZAY4EaOo-aLo0AQroiFCwNWVAxlA+Lvpm2YUHmhbFjWFZ4c2HBkt+lIdgg+QKHY6gPIh+RghkjoPOBALlOBLLcpOqhyCKAZoRKmF1jhFHEAeR4nmeF7Xre95kWA0mfoatEmogM6lMygL2h4misFokGsLSMEZHIXaaAoiHCTC6CDHAojLgiITUW2xriFIk5MUJhiqDoxnMQokFctIVSVPoai5H2Imoe57RhAwnkUu22kINo3xFO43bqJUgLGNy5lAgYCXwuK7TIrAPQyoMaU-nR2iMq6HjWiZaiqNl6iOp4tj2PkVogvY06+ihlWrhh4ZRvwOHxpAu4oGm8AGjRGU+U6ULpCZOgVDOvZuI6lqusB5TmbZvIKBVgboRJ2FbtJjVaZtCgsq6nqzlCbg2OovLHYY6QPH8XF8n9yH+olVWUKmPBQGQwxgBWhD8JAz0bckrhpFoQnupCg5wZxP10lU7jzuC4G6DdYnBqJwwhoQhBwLAV4hqm6PeckkhqP5ciBcFJnTjliC2Wkxk2LkvI2KoPg+EAA */
  id: 'signUpMachine',
  context: ({ input }) => ({ ...initialContext, ...input }),
  initial: 'ready',
  entry: ['sendAuthScreenViewedEvent'],
  states: {
    ready: {
      always: [{ guard: 'isUserLoggedIn', target: 'signUpSuccessful' }, { target: 'chooseMethod' }],
    },
    chooseMethod: {
      invoke: {
        input: ({ context }) => context,
        src: 'resetReCaptcha',
      },
      on: {
        SIGNUP_VIA_CREDENTIALS: {
          actions: [
            'clearStandardAuthErrors',
            'clearOAuthErrors',
            'setAccount',
            'setReCaptchaRef',
            'setOAuthMethod',
            'sendAuthSignUpClickedEvent',
          ],
          target: 'submittingCredentials',
        },
        SIGNUP_VIA_OAUTH: {
          actions: [
            'clearStandardAuthErrors',
            'clearOAuthErrors',
            'setOAuth',
            'sendOAuthButtonClickedEvent',
          ],
          target: 'startingOAuth',
        },
        SWITCH_TO_LOGIN: { actions: ['sendSwitchToLoginEvent'], target: 'loginSelected' },
      },
    },
    submittingCredentials: {
      entry: [{ type: 'setIsLoading', params: { isLoading: true } }],
      invoke: {
        id: 'handleStandardAuth',
        input: ({ context }) => context,
        src: 'handleStandardAuth',
        onDone: { actions: ['setUserIsLoggedInTrue'], target: 'signUpSuccessful' },
        onError: { actions: ['setStandardAuthErrors'], target: 'chooseMethod' },
      },
      exit: [{ type: 'setIsLoading', params: { isLoading: false } }],
    },
    startingOAuth: {
      entry: [{ type: 'setIsLoading', params: { isLoading: true } }],
      invoke: {
        id: 'handleOAuth',
        input: ({ context }) => context,
        src: 'handleOAuth',
        onDone: { actions: ['setUserIsLoggedInTrue'], target: 'signUpSuccessful' },
        onError: { actions: ['setOAuthErrors'], target: 'chooseMethod' },
      },
      exit: [{ type: 'setIsLoading', params: { isLoading: false } }],
    },
    loginSelected: { type: 'final', output: { isSignUpFinished: false } },
    signUpSuccessful: { type: 'final', output: { isSignUpFinished: true } },
  },
  output: ({ event }) => event.output,
});
